import { ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { caseStudies } from "../../shared/case-studies";
import { findCompanyDescription } from "../../shared/company-descriptions";
import { experienceV2 } from "../../shared/experience-v2";
import { openAssistantChat } from "../assistant-events";
import { CompanyLogo } from "./CompanyLogo";

const employerGroups = new Map(
  experienceV2.employerGroups.map((group) => [group.id, group]),
);

const handoffStudies = experienceV2.caseStudiesHandOff.selectedCaseStudyIds.flatMap(
  (id) => {
    const study = caseStudies.studies.find((candidate) => candidate.id === id);
    return study ? [study] : [];
  },
);

function roleOrganisationLabel(role: (typeof experienceV2.timeline)[number]) {
  const employerGroup = role.employerGroupId
    ? employerGroups.get(role.employerGroupId)
    : undefined;

  if (employerGroup) {
    return {
      organisation: employerGroup.organisation,
      companyLookupName: employerGroup.organisation,
      dateRange: employerGroup.dateRange,
    };
  }

  return {
    organisation: role.parallelWorkLabel ?? role.organisation,
    // Parallel entries use a longer display label but retain their company
    // identity for the shared description and logo registries.
    companyLookupName: role.organisation,
  };
}

function ExperienceRole({ role }: { role: (typeof experienceV2.timeline)[number] }) {
  const headingId = `experience-v2-role-${role.id}`;
  const organisation = roleOrganisationLabel(role);
  const companyDescription = findCompanyDescription(organisation.companyLookupName);

  return (
    <article className="experience-v2-role" aria-labelledby={headingId}>
      <span className="experience-v2-date">{role.dateRange}</span>
      <div className="experience-v2-role-content">
        <div className="experience-v2-role-company-line">
          <CompanyLogo companyId={role.id} />
          <span className="experience-v2-role-company">
            {companyDescription
              ? <button className="experience-v2-company-action" type="button" onClick={() => openAssistantChat(`/company-description ${companyDescription.name}`)}>{organisation.organisation}</button>
              : organisation.organisation}
            {organisation.dateRange && <span> | {organisation.dateRange}</span>}
          </span>
        </div>
        <h3 id={headingId}>{role.officialTitle}</h3>
        <p className="experience-v2-role-scope">{role.scope}</p>
        <ul className="experience-v2-evidence">
          {role.evidence.map((item) => (
            <li key={item}>
              <CheckCircle2 size={15} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {role.seniorityNote && <p className="experience-v2-seniority-note">{role.seniorityNote}</p>}
        {role.additionalDetail && role.additionalDetail.length > 0 && (
          <details className="experience-v2-more">
            <summary>More detail</summary>
            {role.additionalDetail.map((detail) => (
              <section key={detail.heading}>
                <h4>{detail.heading}</h4>
                <ul>
                  {detail.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </section>
            ))}
          </details>
        )}
      </div>
    </article>
  );
}

export function ExperienceV2Page() {
  const { page, strengths, timeline, education, caseStudiesHandOff } = experienceV2;

  return (
    <section className="page-view experience-v2-page">
      <header className="experience-v2-header">
        <span className="eyebrow">Career history</span>
        <h1 className="experience-v2-heading">{page.heading}</h1>
        <p className="experience-v2-lead">{page.positioning}</p>
        <p className="experience-v2-availability">{page.availabilityNote}</p>
        <Link className="experience-v2-cross-link" to={page.caseStudiesLink.href}>
          {page.caseStudiesLink.label}<ArrowRight size={16} aria-hidden="true" />
        </Link>
      </header>

      <section className="experience-v2-strengths" aria-label="Selected strengths">
        {strengths.map((strength, index) => (
          <article key={strength}>
            <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
            <strong>{strength}</strong>
          </article>
        ))}
      </section>

      <section className="experience-v2-section" aria-labelledby="professional-experience-title">
        <header className="experience-v2-section-heading">
          <span className="section-kicker">Professional experience</span>
          <h2 id="professional-experience-title">Product leadership across security and technology.</h2>
          <p>Reverse-chronological roles, with independent advisory work clearly shown as parallel engagement.</p>
        </header>
        <div className="experience-v2-timeline">
          {timeline.map((role) => <ExperienceRole key={role.id} role={role} />)}
        </div>
      </section>

      <section className="experience-v2-section" aria-labelledby="education-title">
        <div className="experience-v2-education">
          <div>
            <span className="section-kicker">Education</span>
            <h2 id="education-title">{education.institution}</h2>
          </div>
          <div>
            <p><strong>{education.credential}</strong></p>
            <p>{education.summary}</p>
            {education.additionalDetail && (
              <details className="experience-v2-more">
                <summary>More detail</summary>
                <p>{education.additionalDetail}</p>
              </details>
            )}
          </div>
        </div>
      </section>

      <section className="experience-v2-section" aria-labelledby="case-studies-handoff-title">
        <div className="experience-v2-case-handoff">
          <span className="section-kicker">Case studies</span>
          <h2 id="case-studies-handoff-title">{caseStudiesHandOff.heading}</h2>
          <p>Selected examples of the product decisions, cross-functional work, and evidence behind this experience.</p>
          <div className="experience-v2-case-cards">
            {handoffStudies.map((study) => (
              <Link
                className="experience-v2-case-card"
                key={study.id}
                to={caseStudiesHandOff.link.href}
                aria-label={`View case studies: ${study.title}`}
              >
                <span>{study.theme}</span>
                <h3>{study.title}</h3>
                <p>{study.context}</p>
              </Link>
            ))}
          </div>
          <Link className="primary-action" to={caseStudiesHandOff.link.href}>
            {caseStudiesHandOff.link.label}<ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <p className="experience-v2-trademark-note">
        Company names and logos are trademarks of their respective owners and appear solely to identify prior work experience; no affiliation, sponsorship, or endorsement is implied.
      </p>
    </section>
  );
}

function CaseStudyCard({
  study,
  index,
  expanded,
  onToggle,
}: {
  study: (typeof caseStudies.studies)[number];
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const panelId = `case-study-panel-${study.id}`;
  const headingId = `case-study-heading-${study.id}`;

  return (
    <article className={`case-studies-item${expanded ? " is-open" : ""}`}>
      <h2>
        <button
          type="button"
          className="case-studies-toggle"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="case-studies-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <span className="case-studies-toggle-main">
            <span className="case-studies-meta">{study.theme} <b aria-hidden="true">·</b> {study.dateRange}</span>
            <span id={headingId} className="case-studies-title">{study.title}</span>
            <span className="case-studies-summary">{study.context}</span>
          </span>
          <ChevronDown size={20} aria-hidden="true" />
        </button>
      </h2>

      <div className="case-studies-panel" id={panelId} aria-labelledby={headingId} hidden={!expanded}>
          <section className="case-studies-section">
            <h3>Context / problem</h3>
            <p>{study.context}</p>
          </section>
          <section className="case-studies-section">
            <h3>My role</h3>
            <p>{study.myRole}</p>
          </section>
          <section className="case-studies-section">
            <h3>Team and partners</h3>
            <div className="case-studies-partners">
              {study.teamAndPartners.map((partner) => <span key={partner}>{partner}</span>)}
            </div>
          </section>
          <section className="case-studies-section">
            <h3>Approach and decisions</h3>
            <ul className="case-studies-section-list">
              {study.approachAndDecisions.map((decision) => (
                <li key={decision}>
                  <CheckCircle2 size={15} aria-hidden="true" />
                  <span>{decision}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="case-studies-section">
            <h3>Outcome / evidence</h3>
            <div className="case-studies-outcomes">
              {study.outcomeAndEvidence.map((outcome) => (
                <article className="case-studies-outcome" key={outcome.label}>
                  <strong>{outcome.label}</strong>
                  <span>{outcome.description}</span>
                </article>
              ))}
            </div>
          </section>
          <section className="case-studies-section">
            <h3>Source links</h3>
            {study.sourceLinks.length > 0 ? (
              <div className="case-studies-source-links">
                {study.sourceLinks.map((link) => (
                  <a key={link.url} href={link.url} target="_blank" rel="noreferrer">
                    {link.label}<ExternalLink size={13} aria-hidden="true" />
                  </a>
                ))}
              </div>
            ) : (
              <p>No public source links are available for this case study.</p>
            )}
          </section>
      </div>
    </article>
  );
}

export function CaseStudiesPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { page, studies } = caseStudies;

  return (
    <section className="page-view case-studies-page">
      <header className="case-studies-header">
        <div className="case-studies-header-copy">
          <span className="eyebrow">Selected work</span>
          <h1 className="case-studies-heading">{page.heading}</h1>
          <p>{page.intro}</p>
        </div>
        <Link className="case-studies-cross-link" to={page.experienceLink.href}>
          <ArrowLeft size={16} aria-hidden="true" />{page.experienceLink.label}
        </Link>
      </header>

      <div className="case-studies-accordion">
        {studies.map((study, index) => (
          <CaseStudyCard
            key={study.id}
            study={study}
            index={index}
            expanded={openId === study.id}
            onToggle={() => setOpenId((current) => current === study.id ? null : study.id)}
          />
        ))}
      </div>
    </section>
  );
}
